import type { ShellId } from './types';

export const NAVIGATION_TELEMETRY_EVENT = 'aa:navigation-telemetry';
export const NAVIGATION_TELEMETRY_NAMES = [
  'reconstruction_failed',
  'leaf_evicted',
  'origin_fallback',
  'canonicalization_loop',
] as const;

export type NavigationTelemetryName = (typeof NAVIGATION_TELEMETRY_NAMES)[number];
export type NavigationTelemetryReason =
  | 'render-error' | 'restore-miss' | 'lru-limit'
  | 'invalid-origin' | 'unauthorized-origin' | 'alias-loop';

export interface NavigationTelemetryInput {
  name: NavigationTelemetryName;
  routeId?: string;
  shell?: ShellId;
  reason?: NavigationTelemetryReason;
}

export type NavigationTelemetryRecord = Readonly<NavigationTelemetryInput & { occurredAt: number }>;
export type NavigationTelemetrySink = (event: NavigationTelemetryRecord) => void;

let sink: NavigationTelemetrySink | null = null;
const counts = new Map<NavigationTelemetryName, number>();

export function setNavigationTelemetrySink(next: NavigationTelemetrySink | null): void {
  sink = next;
}

export function recordNavigationTelemetry(input: NavigationTelemetryInput): void {
  const event: NavigationTelemetryRecord = Object.freeze({
    name: input.name,
    ...(input.routeId ? { routeId: input.routeId } : {}),
    ...(input.shell ? { shell: input.shell } : {}),
    ...(input.reason ? { reason: input.reason } : {}),
    occurredAt: Date.now(),
  });
  counts.set(event.name, (counts.get(event.name) ?? 0) + 1);
  sink?.(event);
  if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NAVIGATION_TELEMETRY_EVENT, { detail: event }));
  }
}

export function getNavigationTelemetryCounts(): Record<NavigationTelemetryName, number> {
  return Object.fromEntries(
    NAVIGATION_TELEMETRY_NAMES.map((name) => [name, counts.get(name) ?? 0]),
  ) as Record<NavigationTelemetryName, number>;
}
